/**
 * Wedding pre-order menu (Stekki's / Ammos Kalambouri).
 *
 * Structure:
 *   MENU_CATEGORIES:  [{ id, label, emoji, items: [...] }]
 *   each item:        { id, name, desc?, variants?: [string, ...] }
 *
 * If `variants` is set, the UI renders one stepper row per variant so guests
 * can order e.g. 2 vanilla + 1 chocolate milkshake as two distinct lines.
 */

export const MENU_CATEGORIES = [
  {
    id: "sandwiches",
    label: "Sandwiches",
    emoji: "🥪",
    items: [
      {
        id: "sw-special",
        name: "Stekki's Special Sandwich",
        desc: "Ham, bacon, lunza, cheese, egg, fresh tomato and cucumber.",
      },
      {
        id: "sw-mix",
        name: "Mix Sandwich",
        desc: "Ham, bacon, lunza, cheese, fresh tomato and cucumber.",
      },
      {
        id: "sw-tuna",
        name: "Tuna Sandwich",
        desc: "Tuna, lettuce, tomato, cucumber and onion.",
      },
    ],
  },
  {
    id: "burgers",
    label: "Burgers",
    emoji: "🍔",
    items: [
      {
        id: "bg-special",
        name: "Stekki's Special Burger",
        desc: "Grilled beef burger topped with bacon, fried egg, fresh tomato and cucumber.",
      },
      {
        id: "bg-cheese",
        name: "Cheese Burger",
        desc: "Grilled beef burger with cheese, fresh tomato and cucumber.",
      },
      {
        id: "bg-plain",
        name: "Burger",
        desc: "Grilled beef burger with fresh tomato and cucumber.",
      },
      {
        id: "bg-chicken",
        name: "Chicken Burger",
        desc: "Breaded chicken burger with lettuce, tomato and cucumber.",
      },
      {
        id: "bg-veggie",
        name: "Veggie Burger",
        desc: "Vegetarian burger with lettuce, tomato and cucumber.",
      },
      {
        id: "bg-classic",
        name: "Beefburger",
        desc: "Classic beef burger with tomato, cucumber and ketchup.",
      },
    ],
  },
  {
    id: "pittas",
    label: "Pittas & Doners",
    emoji: "🌯",
    items: [
      {
        id: "pt-chicken-doner",
        name: "Chicken Doner",
        desc: "Chicken doner in warm pitta with fresh salad and your choice of tzatziki or chilli sauce.",
      },
      {
        id: "pt-beef-doner",
        name: "Beef Doner",
        desc: "Beef doner in warm pitta with fresh salad and your choice of tzatziki or chilli sauce.",
      },
      {
        id: "pt-tuna",
        name: "Tuna Pitta",
        desc: "Tuna with lettuce, tomato, cucumber and onion in warm pitta.",
      },
    ],
  },
  {
    id: "hotdog",
    label: "Hot Dog",
    emoji: "🌭",
    items: [
      {
        id: "hd-hotdog",
        name: "Hot Dog",
        desc: "Served with onions and your choice of mustard, mayonnaise or ketchup.",
      },
    ],
  },
  {
    id: "salads",
    label: "Salads",
    emoji: "🥗",
    items: [
      {
        id: "sl-village",
        name: "Village Salad",
        desc: "Fresh lettuce, tomato, cucumber and onion, olive oil and vinegar dressing.",
      },
      {
        id: "sl-tuna",
        name: "Tuna Salad",
        desc: "Fresh salad topped with tuna, dressed with olive oil and vinegar.",
      },
    ],
  },
  {
    id: "omelettes",
    label: "Omelettes",
    emoji: "🍳",
    items: [
      {
        id: "om-plain",
        name: "Plain Omelette",
        desc: "Freshly prepared omelette served with a side salad.",
      },
      {
        id: "om-hb",
        name: "Ham & Bacon Omelette",
        desc: "Omelette with ham and bacon, served with a side salad.",
      },
    ],
  },
  {
    id: "sides",
    label: "Sides",
    emoji: "🍟",
    items: [
      { id: "sd-chips", name: "Chips", desc: "Golden crispy fries." },
    ],
  },
  {
    id: "cold-drinks",
    label: "Cold Drinks",
    emoji: "🥤",
    items: [
      { id: "cd-soft", name: "Soft Drink", desc: "Coke, Sprite, Fanta and the classics." },
      { id: "cd-icetea", name: "Ice Tea" },
      {
        id: "cd-oj",
        name: "Fresh Orange Juice",
        desc: "Freshly squeezed.",
        variants: ["Small", "Large"],
      },
    ],
  },
  {
    id: "hot-drinks",
    label: "Hot Drinks",
    emoji: "☕",
    items: [
      { id: "hd-tea", name: "Tea" },
    ],
  },
  {
    id: "milkshakes",
    label: "Milkshakes",
    emoji: "🥤",
    items: [
      {
        id: "ms",
        name: "Milkshake",
        variants: ["Vanilla", "Strawberry", "Banana", "Chocolate"],
      },
    ],
  },
  {
    id: "icecream",
    label: "Ice Cream",
    emoji: "🍦",
    items: [
      {
        id: "ic",
        name: "Ice Cream",
        variants: ["Vanilla", "Strawberry", "Banana", "Chocolate"],
      },
    ],
  },
  {
    id: "snacks",
    label: "Snacks",
    emoji: "🍿",
    items: [
      { id: "sn-crisps", name: "Crisps", desc: "A selection of potato crisps." },
    ],
  },
];

/**
 * Flatten menu into ordered [{key, id, name, variant, desc, categoryId}] rows,
 * one row per stepper. Items with variants → one row per variant.
 */
export const flattenMenuRows = () => {
  const rows = [];
  for (const cat of MENU_CATEGORIES) {
    for (const item of cat.items) {
      if (item.variants && item.variants.length > 0) {
        for (const variant of item.variants) {
          rows.push({
            key: `${item.id}::${variant}`,
            id: item.id,
            name: item.name,
            variant,
            desc: item.desc,
            categoryId: cat.id,
            categoryLabel: cat.label,
          });
        }
      } else {
        rows.push({
          key: item.id,
          id: item.id,
          name: item.name,
          variant: null,
          desc: item.desc,
          categoryId: cat.id,
          categoryLabel: cat.label,
        });
      }
    }
  }
  return rows;
};

/**
 * Turn a counts map ({key: qty}) into a compact preorder payload.
 *   [{ id, name, variant, quantity }]
 */
export const buildPreorderPayload = (counts) => {
  const rows = flattenMenuRows();
  const out = [];
  for (const row of rows) {
    const qty = Number(counts[row.key]) || 0;
    if (qty > 0) {
      out.push({
        id: row.id,
        name: row.name,
        variant: row.variant || null,
        quantity: qty,
      });
    }
  }
  return out;
};

/** Human summary string, e.g. "2× Cheese Burger, 1× Milkshake · Vanilla". */
export const summarisePreorder = (preorder) => {
  if (!Array.isArray(preorder) || preorder.length === 0) return "";
  return preorder
    .map((it) => `${it.quantity}× ${it.name}${it.variant ? ` · ${it.variant}` : ""}`)
    .join(", ");
};

/** Reverse of buildPreorderPayload — turns a stored preorder array back into
 * a { [rowKey]: qty } map so we can prefill the MenuPreorder component when
 * a guest edits their existing RSVP. */
export const itemsToCounts = (preorder) => {
  const rows = flattenMenuRows();
  const counts = {};
  if (!Array.isArray(preorder)) return counts;
  for (const item of preorder) {
    if (!item) continue;
    const match = rows.find((r) =>
      r.id === item.id &&
      ((r.variant || null) === (item.variant || null))
    );
    if (match) counts[match.key] = Number(item.quantity) || 0;
  }
  return counts;
};
